import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import {
  referralAPI,
  ReferralSummary,
  RefereeItem,
  BonusLogItem,
} from '../services/api';
import './AffiliatePage.css';

const formatRub = (value: string | number) => {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(num) ? num.toFixed(2) : '0.00';
};

const formatDate = (iso: string, locale: string) =>
  new Date(iso).toLocaleString(locale === 'en' ? 'en-US' : 'ru-RU');

// ---------- Convert modal ----------
const ConvertModal: React.FC<{
  available: string;
  rate: number;
  onClose: () => void;
  onSuccess: () => void;
}> = ({ available, rate, onClose, onSuccess }) => {
  const { t } = useTranslation('profile');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const numeric = parseFloat(amount || '0') || 0;
  const ticksPreview = Math.round(numeric * rate);

  const handleSubmit = async () => {
    if (numeric <= 0) {
      setError(t('affiliate.convert_modal.error_positive', 'Сумма должна быть положительной'));
      return;
    }
    if (numeric > parseFloat(available)) {
      setError(t('affiliate.convert_modal.error_too_much', 'Сумма превышает доступный баланс'));
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await referralAPI.convertToTicks(amount);
      onSuccess();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Ошибка');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="affiliate-modal-overlay" onClick={onClose}>
      <div className="affiliate-modal" onClick={(e) => e.stopPropagation()}>
        <h3>{t('affiliate.convert_modal.title', 'Конвертация в тики')}</h3>
        <label>{t('affiliate.convert_modal.input_label', 'Сумма (₽)')}</label>
        <input
          type="number"
          min="0"
          step="0.01"
          max={available}
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          placeholder={`max ${formatRub(available)}`}
        />
        {numeric > 0 && (
          <div className="preview">
            {t('affiliate.convert_modal.preview', 'Будет начислено')}: <strong>{ticksPreview}</strong>{' '}
            {t('affiliate.convert_modal.ticks', 'тиков')}
          </div>
        )}
        {error && <div className="affiliate-error">{error}</div>}
        <div className="affiliate-modal-buttons">
          <button className="cancel" onClick={onClose} disabled={submitting}>
            {t('affiliate.convert_modal.cancel', 'Отмена')}
          </button>
          <button className="submit" onClick={handleSubmit} disabled={submitting || numeric <= 0}>
            {submitting
              ? t('affiliate.convert_modal.submitting', 'Конвертация...')
              : t('affiliate.convert_modal.submit', 'Конвертировать')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Tooltip ----------
const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="info-tip" tabIndex={0} aria-label={text} title={text}>
    ⓘ
    <span className="info-tip-bubble">{text}</span>
  </span>
);

// ---------- Stat card ----------
const StatCard: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: string;
  variant?: 'default' | 'primary' | 'muted';
}> = ({ label, value, hint, variant = 'default' }) => (
  <div className={`affiliate-stat-card variant-${variant}`}>
    <div className="label-row">
      <span className="label">{label}</span>
      {hint && <InfoTip text={hint} />}
    </div>
    <div className="value">{value}</div>
  </div>
);

// ---------- AffiliatePage ----------
const AffiliatePage: React.FC = () => {
  const { t, i18n } = useTranslation('profile');
  const { refreshUser } = useAuth();
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [referees, setReferees] = useState<RefereeItem[]>([]);
  const [history, setHistory] = useState<BonusLogItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copyState, setCopyState] = useState('');
  const [convertOpen, setConvertOpen] = useState(false);

  // Strip stale query params (e.g. ?taskId=...) leftover from previous
  // navigation — they don't belong on /affiliate and clutter share/refresh.
  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [s, r, h] = await Promise.all([
        referralAPI.getSummary(),
        referralAPI.listReferees(50, 0),
        referralAPI.listHistory(100, 0),
      ]);
      setSummary(s);
      setReferees(r);
      setHistory(h);
    } catch (e) {
      console.error('Failed to load affiliate data', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleCopy = async () => {
    if (!summary) return;
    try {
      await navigator.clipboard.writeText(summary.share_url);
      setCopyState(t('affiliate.copied', 'Скопировано!'));
      setTimeout(() => setCopyState(''), 2000);
    } catch {
      // fallback: select text
    }
  };

  const buildShareText = () =>
    t(
      'affiliate.share_text',
      'Создавай аналитические отчёты в AI Reports — попробуй по моей ссылке:',
    ) + ' ' + (summary?.share_url || '');

  const openShare = (url: string) => window.open(url, '_blank', 'noopener,noreferrer');

  if (loading || !summary) {
    return (
      <div className="affiliate-page">
        <div className="affiliate-loading">{t('loading', 'Загрузка...')}</div>
      </div>
    );
  }

  const balance = parseFloat(summary.bonus_balance_rub);
  const rate = summary.rub_to_ticks_rate;
  const shareText = buildShareText();
  const tgUrl = `https://t.me/share/url?url=${encodeURIComponent(
    summary.share_url,
  )}&text=${encodeURIComponent(
    t('affiliate.share_text', 'Создавай аналитические отчёты в AI Reports — попробуй по моей ссылке:'),
  )}`;
  const waUrl = `https://wa.me/?text=${encodeURIComponent(shareText)}`;

  return (
    <div className="affiliate-page">
      <h1 className="affiliate-title">{t('affiliate.title', 'Партнёрская программа')}</h1>
      <p className="affiliate-subtitle">
        {t(
          'affiliate.subtitle',
          'Делитесь сервисом — получайте % с каждого платежа приглашённого друга и тики за его активность.',
        )}
      </p>

      {/* Hero with share link + share buttons */}
      <div className="affiliate-hero">
        <div className="hero-content">
          <div className="hero-badge">
            {t('affiliate.hero_badge', '+100 тиков вам и другу за регистрацию')}
          </div>
          <h2 className="hero-title">
            {t('affiliate.your_link', 'Ваша реферальная ссылка')}
          </h2>
          <div className="share-link">
            <code>{summary.share_url}</code>
            <button className="copy-button" onClick={handleCopy}>
              {copyState || t('affiliate.copy', 'Копировать')}
            </button>
          </div>
          <div className="share-row">
            <span className="share-row-label">
              {t('affiliate.share_via', 'Поделиться:')}
            </span>
            <button
              className="share-btn share-btn-tg"
              onClick={() => openShare(tgUrl)}
              aria-label="Telegram"
              title="Telegram"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M9.78 18.65l.28-4.23 7.68-6.92c.34-.31-.07-.46-.52-.19L7.74 13.3 3.64 12c-.88-.25-.89-.86.2-1.3l15.97-6.16c.73-.33 1.43.18 1.15 1.3l-2.72 12.81c-.19.91-.74 1.13-1.5.7L12.6 16.3l-1.99 1.93c-.23.23-.42.42-.83.42z"
                />
              </svg>
              Telegram
            </button>
            <button
              className="share-btn share-btn-wa"
              onClick={() => openShare(waUrl)}
              aria-label="WhatsApp"
              title="WhatsApp"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path
                  fill="currentColor"
                  d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.515 5.26l-.999 3.648 3.973-1.607zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.074-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.371-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z"
                />
              </svg>
              WhatsApp
            </button>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="affiliate-how-it-works">
        <h3>{t('affiliate.how_title', 'Как это работает')}</h3>
        <div className="how-steps">
          <div className="how-step">
            <div className="how-step-num">1</div>
            <div className="how-step-title">
              {t('affiliate.how_step1_title', 'Поделитесь ссылкой')}
            </div>
            <div className="how-step-text">
              {t(
                'affiliate.how_step1_text',
                'Отправьте свою реферальную ссылку друзьям и коллегам.',
              )}
            </div>
          </div>
          <div className="how-step">
            <div className="how-step-num">2</div>
            <div className="how-step-title">
              {t('affiliate.how_step2_title', 'Друг регистрируется')}
            </div>
            <div className="how-step-text">
              {t(
                'affiliate.how_step2_text',
                'Вы оба получаете +100 тиков сразу после регистрации по ссылке.',
              )}
            </div>
          </div>
          <div className="how-step">
            <div className="how-step-num">3</div>
            <div className="how-step-title">
              {t('affiliate.how_step3_title', 'Друг покупает тики')}
            </div>
            <div className="how-step-text">
              {t(
                'affiliate.how_step3_text',
                'Вам начисляется бонус-кэшбек с каждого его платежа. Вывод после холда — на тики или деньги.',
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="affiliate-stats">
        <StatCard
          label={t('affiliate.stats.invited', 'Приглашено')}
          value={summary.invited_count}
          hint={t(
            'affiliate.stats.invited_hint',
            'Сколько пользователей зарегистрировались по вашей ссылке.',
          )}
        />
        <StatCard
          label={t('affiliate.stats.earned_ticks', 'Заработано тиков')}
          value={summary.earned_ticks_total}
          hint={t(
            'affiliate.stats.earned_ticks_hint',
            'Сумма всех начисленных вам реферальных тиков.',
          )}
        />
        <StatCard
          variant="primary"
          label={t('affiliate.stats.balance_rub', 'Доступно к выводу ₽')}
          value={
            <>
              {formatRub(summary.bonus_balance_rub)}
              {balance > 0 && (
                <span className="value-suffix">
                  ≈ {Math.round(balance * rate)}{' '}
                  {t('affiliate.convert_modal.ticks', 'тиков')}
                </span>
              )}
            </>
          }
          hint={t(
            'affiliate.stats.balance_hint',
            'Подтверждённый бонус, который можно перевести в тики или вывести.',
          )}
        />
        <StatCard
          variant="muted"
          label={t('affiliate.stats.on_hold', 'На удержании ₽')}
          value={formatRub(summary.pending_rub)}
          hint={t(
            'affiliate.stats.on_hold_hint',
            'Бонусы по свежим платежам — подтверждаются автоматически через 14 дней (антифрод).',
          )}
        />
        <StatCard
          variant="muted"
          label={t('affiliate.stats.paid_out', 'Выплачено ₽')}
          value={formatRub(summary.paid_out_rub)}
          hint={t(
            'affiliate.stats.paid_out_hint',
            'Сумма всех ранее выведенных бонусов (на тики или деньги).',
          )}
        />
      </div>

      {/* Convert action */}
      <div className="affiliate-convert">
        <div className="convert-info">
          <div className="convert-rate">
            {t('affiliate.rate_prefix', 'Курс конвертации')}:{' '}
            <strong>1 ₽ = {rate} {t('affiliate.convert_modal.ticks', 'тиков')}</strong>
          </div>
          <div className="convert-balance">
            {t('affiliate.available_now', 'Доступно сейчас')}:{' '}
            <strong>{formatRub(summary.bonus_balance_rub)} ₽</strong>
          </div>
        </div>
        <button
          className="convert-button"
          onClick={() => setConvertOpen(true)}
          disabled={balance <= 0}
        >
          {t('affiliate.convert_to_ticks', 'Перевести в тики')}
        </button>
      </div>

      {/* Referees */}
      <div className="affiliate-section">
        <h3>{t('affiliate.referees', 'Мои рефералы')}</h3>
        {referees.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">👥</div>
            <div className="empty-title">
              {t('affiliate.no_referees', 'Пока никого нет — поделитесь ссылкой!')}
            </div>
            <div className="empty-cta-row">
              <button className="empty-cta" onClick={handleCopy}>
                {copyState || t('affiliate.copy_to_share', 'Скопировать ссылку')}
              </button>
              <button className="empty-cta secondary" onClick={() => openShare(tgUrl)}>
                {t('affiliate.share_telegram', 'Поделиться в Telegram')}
              </button>
            </div>
          </div>
        ) : (
          <>
            <table className="affiliate-table desktop-only">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>{t('affiliate.col.created_at', 'Дата')}</th>
                  <th>{t('affiliate.col.payments', 'Платежей')}</th>
                  <th>{t('affiliate.col.ticks_spent', 'Потрачено тиков')}</th>
                  <th>{t('affiliate.col.confirmed', 'Подтверждено ₽')}</th>
                  <th>{t('affiliate.col.pending', 'В удержании ₽')}</th>
                </tr>
              </thead>
              <tbody>
                {referees.map((r) => (
                  <tr key={r.id}>
                    <td>{r.email_masked}</td>
                    <td>{formatDate(r.created_at, i18n.language)}</td>
                    <td>{r.payments_count}</td>
                    <td>{r.ticks_spent}</td>
                    <td>{formatRub(r.cash_rebate_confirmed)}</td>
                    <td>{formatRub(r.cash_rebate_pending)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="affiliate-cards mobile-only">
              {referees.map((r) => (
                <div key={r.id} className="affiliate-row-card">
                  <div className="row-card-head">
                    <strong>{r.email_masked}</strong>
                    <span>{formatDate(r.created_at, i18n.language)}</span>
                  </div>
                  <div className="row-card-grid">
                    <div>
                      <div className="rc-label">{t('affiliate.col.payments', 'Платежей')}</div>
                      <div className="rc-value">{r.payments_count}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.ticks_spent', 'Потрачено тиков')}</div>
                      <div className="rc-value">{r.ticks_spent}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.confirmed', 'Подтверждено ₽')}</div>
                      <div className="rc-value">{formatRub(r.cash_rebate_confirmed)}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.pending', 'В удержании ₽')}</div>
                      <div className="rc-value">{formatRub(r.cash_rebate_pending)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* History */}
      <div className="affiliate-section">
        <h3>{t('affiliate.history', 'История начислений')}</h3>
        {history.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon" aria-hidden="true">📒</div>
            <div className="empty-title">
              {t(
                'affiliate.no_history',
                'История начислений появится после первой активности приглашённого.',
              )}
            </div>
          </div>
        ) : (
          <>
            <table className="affiliate-table desktop-only">
              <thead>
                <tr>
                  <th>{t('affiliate.col.date', 'Дата')}</th>
                  <th>{t('affiliate.col.type', 'Тип')}</th>
                  <th>{t('affiliate.col.status', 'Статус')}</th>
                  <th>{t('affiliate.col.amount', 'Сумма ₽')}</th>
                  <th>{t('affiliate.col.referee', 'Реферал')}</th>
                  <th>{t('affiliate.col.description', 'Описание')}</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDate(h.created_at, i18n.language)}</td>
                    <td>{h.operation_type}</td>
                    <td>
                      <span className={`badge ${h.status}`}>{h.status}</span>
                    </td>
                    <td>{formatRub(h.amount)}</td>
                    <td>{h.referee_email_masked || '—'}</td>
                    <td>{h.description || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="affiliate-cards mobile-only">
              {history.map((h) => (
                <div key={h.id} className="affiliate-row-card">
                  <div className="row-card-head">
                    <strong>{h.operation_type}</strong>
                    <span className={`badge ${h.status}`}>{h.status}</span>
                  </div>
                  <div className="row-card-grid">
                    <div>
                      <div className="rc-label">{t('affiliate.col.date', 'Дата')}</div>
                      <div className="rc-value">{formatDate(h.created_at, i18n.language)}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.amount', 'Сумма ₽')}</div>
                      <div className="rc-value">{formatRub(h.amount)}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.referee', 'Реферал')}</div>
                      <div className="rc-value">{h.referee_email_masked || '—'}</div>
                    </div>
                    <div>
                      <div className="rc-label">{t('affiliate.col.description', 'Описание')}</div>
                      <div className="rc-value">{h.description || '—'}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {convertOpen && (
        <ConvertModal
          available={summary.bonus_balance_rub}
          rate={summary.rub_to_ticks_rate}
          onClose={() => setConvertOpen(false)}
          onSuccess={() => {
            setConvertOpen(false);
            refreshUser();
            load();
          }}
        />
      )}
    </div>
  );
};

export default AffiliatePage;
